import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFeedback } from './FeedbackProvider';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import FeedbackImpactReceipt from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { ResizableSplitView } from './ResizableSplitView';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { DECORATORS } from 'shared/design-system/settings/variables/Decorators';
import FeedbackHeader from './FeedbackHeader';
import Feedback from './Feedback';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { AnswersProvider } from './AnswersProvider';
import { FeedbackProvider } from './FeedbackProvider';
import { RouteComponentProps } from 'react-router-dom';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';

const StyledDiv = styled.div<{ isCollapsed: boolean }>`
  .collapsible-header {
    background-color: ${COLORS.grayLight};
    transition: height 0.3s ease;
    overflow: hidden;
  }

  .feedback-content {
    width: 100%;
    background-color: ${COLORS.grayLightest};
    transition: height 0.3s ease;

    .resizable-content {
      height: 100%;
      overflow: auto;
      ${thinScrollbarStyles}
    }

    .right-content {
      padding: ${SPACERS.large} ${SPACERS.medium} 0 ${SPACERS.medium};
      background-color: ${COLORS.whiteBase};
      height: 100%;
      overflow: auto;
      ${({ isCollapsed }) =>
        isCollapsed ? `border-top: 1px solid ${COLORS.grayLight};` : ''}
      ${thinScrollbarStyles}
    }
  }

  .toggle-button {
    position: fixed;
    top: 0%;
    right: ${SPACERS.large};
    z-index: 9999;
    background-color: ${COLORS.ctaPrimary};
    color: white;
    border: none;
    cursor: pointer;
    padding: 10px ${SPACERS.small};
    // border-left: 1px solid ${COLORS.whiteBase};
    // border-right: 1px solid ${COLORS.whiteBase};
    // border-bottom: 1px solid ${COLORS.whiteBase};
    box-shadow: ${DECORATORS.boxShadowDefault};
    border-bottom-left-radius: ${SPACERS.extraTiny};
    border-bottom-right-radius: ${SPACERS.extraTiny};
  }
`;

export const BetaFeedbackWithProvidersAndReportId: React.FC<{
  reportId: string | number;
}> = ({ reportId }) => {
  let topHeight = 48;
  const { pathname } = window.location;
  if (pathname.includes('/account/')) {
    topHeight = 52;
  }
  return (
    <AnswersProvider reportId={reportId}>
      <FeedbackProvider reportId={reportId}>
        <BetaFeedback topHeight={topHeight} />
      </FeedbackProvider>
    </AnswersProvider>
  );
};

export const BetaFeedbackWithProviders: React.FC<RouteComponentProps<{
  feedbackId: string;
}>> = ({ match }) => {
  return (
    <BetaFeedbackWithProvidersAndReportId reportId={match.params.feedbackId} />
  );
};

export const FeedbackRender: React.FC<{
  topHeight?: number;
  rightComponent: React.FC;
  topComponent: React.ReactNode | React.ReactNode[];
  leftComponent: React.ReactNode | React.ReactNode[];
}> = ({ topHeight = 48, topComponent, leftComponent, rightComponent }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.scrollHeight;
      setHeaderHeight(isCollapsed ? 0 : height);
    }
  }, [isCollapsed]);

  return (
    <StyledDiv isCollapsed={isCollapsed}>
      <div
        className="collapsible-header"
        ref={headerRef}
        style={{ height: `${headerHeight}px` }}
      >
        {topComponent}
      </div>
      <div
        className="feedback-content"
        style={{ height: `calc(100vh - ${topHeight + headerHeight}px)` }}
      >
        <ResizableSplitView
          defaultWidth={50}
          minWidth={30}
          maxWidth={70}
          left={leftComponent}
          right={
            <FeedbackRightContent
              isCollapsed={isCollapsed}
              Component={rightComponent}
              setIsCollapsed={setIsCollapsed}
            />
          }
        />
      </div>
    </StyledDiv>
  );
};

const BetaFeedback: React.FC<{ topHeight?: number }> = ({ topHeight = 48 }) => {
  const { feedback, answers, userStaticType } = useFeedback();
  const { report, isReportEndDatePassed } = useReport();
  const trackEvent = useTrackEvent();
  const reportId = feedback.reportId || feedback.id;

  useEffect(() => {
    const reportData = report.data;
    if (!reportData || !reportData.stage) return;
    trackEvent(
      'Feedback',
      pullReportData(reportData, isReportEndDatePassed, userStaticType),
    );
  }, [report]);

  return (
    <FeedbackRender
      topHeight={topHeight}
      topComponent={<FeedbackHeader feedback={feedback} />}
      leftComponent={
        <FeedbackImpactReceiptComponent
          reportId={reportId}
          programName={feedback.title}
          answers={answers}
          feedback={feedback}
        />
      }
      rightComponent={Feedback}
    />
  );
};

const FeedbackImpactReceiptComponent = React.memo(
  ({
    reportId,
    programName,
    answers,
    feedback,
  }: {
    reportId: number;
    programName: string;
    answers: ReportAnswers;
    feedback: FeedbackDashboardCardProps;
  }) => {
    return (
      <div className="resizable-content">
        <FeedbackImpactReceipt
          isThirdParty={false}
          reportId={reportId}
          programName={programName}
          answers={answers}
          feedback={feedback}
        />
      </div>
    );
  },
);

const FeedbackRightContent: React.FC<{
  isCollapsed: boolean;
  Component: React.FC;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ isCollapsed, setIsCollapsed, Component }) => {
  return (
    <div className="right-content">
      <button
        className="toggle-button"
        onClick={() => setIsCollapsed(prev => !prev)}
      >
        {isCollapsed ? (
          <FontAwesomeIcon icon={faChevronDown} />
        ) : (
          <FontAwesomeIcon icon={faChevronUp} />
        )}
      </button>
      <Component />
    </div>
  );
};

export default BetaFeedback;
